import { useMemo, useEffect } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';

import { useOrder, SET_ORDER_DETAILS } from '../../hooks/useOrder';
import { useAuth } from '../../hooks/useAuth';
import { getProduct } from '../../helpers/products';
import OrderPage from '../../components/payments/OrderPage';
import Button from '../../components/inputs/Button';
import MyOrder from '../../components/payments/MyOrder';
import { canOrderAudienceProfile } from '../../utils';

export default function OrderAudience() {
  const { state, dispatch, loadOrder, openOrder } = useOrder();
  const { orderStatus, products } = state;
  const { user } = useAuth();
  const { plan } = user;

  const location = useLocation();
  const { state: routerState } = location;

  const orderDetails = useMemo(() => {
    // Start with state.orderDetails from context
    let orderDetails = state?.orderDetails || {};

    // Merge in router state if provided
    if (routerState?.orderDetails) {
      orderDetails = { ...orderDetails, ...routerState.orderDetails };
    }

    // Only merge URL params if we don't have the required fields and URL params exist
    if ((!orderDetails.orderId || !orderDetails.audienceName)) {
      const params = new URLSearchParams(window.location.search);
      const urlOrderId = params.get('orderId');
      const urlCoreProductName = params.get('coreProductName');
      const urlAudienceName = params.get('audienceName');
      const urlRecordsIncluded = params.get('recordsIncluded');

      const orderDetailsParamValues = {};

      // Only add to orderDetails if URL param exists
      if (urlOrderId) orderDetailsParamValues.orderId = urlOrderId;
      if (urlCoreProductName) orderDetailsParamValues.coreProductName = urlCoreProductName;
      if (urlAudienceName) orderDetailsParamValues.audienceName = urlAudienceName;
      if (urlRecordsIncluded) orderDetailsParamValues.recordsIncluded = Number(urlRecordsIncluded);

      orderDetails = { ...orderDetails, ...orderDetailsParamValues };
    }

    return orderDetails;
  }, [routerState?.orderDetails, state?.orderDetails]);

  const {
    orderId,
    audienceName,
    coreProductName,
    recordsIncluded,
    // PR 2d: the saved Audience's id and the client-rendered plain-language
    // summary. Both ride into openOrder so the server can persist the
    // audienceSnapshot. Without `audience` the server short-circuits and
    // the admin CSV columns (Audience Summary, Snapshot Captured At) stay
    // empty. This page is reached when Stripe payment is disabled and
    // AudienceUpsell auto-redirects here, so the same forwarding rules apply.
    audience,
    audiencePlainText,
  } = orderDetails;

  useEffect(() => {
    if (
      Object.keys(state?.orderDetails).length === 0 &&
      Object.keys(state?.orderDetails).length > 0
    ) {
      dispatch({
        type: SET_ORDER_DETAILS,
        payload: orderDetails,
      });
    }
  }, [orderDetails, dispatch, state?.orderDetails]);

  useEffect(() => {
    if (orderId && orderStatus == null) {
      loadOrder({
        variables: {
          orderId,
          expandInvoice: true,
        },
      });
    }
  }, [loadOrder, orderId, orderStatus, state?.orderDetails]);

  // If we don't have an orderId but have order details, create the order
  useEffect(() => {
    if (!orderId && coreProductName && audienceName && recordsIncluded) {
      // Get the core product
      const product = getProduct(coreProductName, plan);

      if (product) {
        openOrder({
          variables: {
            products: [{ ...product, quantity: recordsIncluded }],
            orderType: coreProductName,
            orderDetails: {
              audienceName,
              recordsIncluded,
              // PR 2d: forward the Audience id + plain-language summary
              // when present so the server can capture the snapshot.
              ...(audience ? { audience } : {}),
              ...(audiencePlainText ? { audiencePlainText } : {}),
            },
          },
        });
      }
    }
  }, [
    orderId,
    coreProductName,
    audienceName,
    recordsIncluded,
    openOrder,
    products,
    plan,
    audience,
    audiencePlainText,
  ]);

  // "Complete and Profile": completes the audience order AND auto-orders a
  // profile report for it, so Ask Addy users never have to visit the manual
  // profile flow. Gated to the roles the server accepts for audience-profile
  // orders (superUserProtected).
  const secondaryComplete = useMemo(() => {
    if (!canOrderAudienceProfile(user) || !recordsIncluded) {
      return null;
    }
    return {
      label: 'Complete and Profile',
      variables: {
        alsoOrderProfile: true,
        profileProducts: [
          {
            ...getProduct('audience-profile', plan),
            quantity: Number(recordsIncluded),
          },
        ],
      },
    };
  }, [user, recordsIncluded, plan]);

  if (Object.keys(orderDetails).length === 0) {
    return <Navigate to="../../../mywork/audiences" />;
  }

  return (
    <OrderPage
      productLabel={<ProductLabel audienceName={audienceName} />}
      productPricing={null}
      productDetails={null}
      orderSummary={<ProductDetails audienceName={audienceName} />}
      backButton={<BackButton />}
      showTotal={false}
      secondaryComplete={secondaryComplete}
    />
  );
}

function ProductLabel({ audienceName }) {
  return (
    <p>
      {/*Audience Name: <span className="font-bold">{audienceName}</span>*/}
    </p>
  );
}

function ProductDetails({ audienceName }) {
  if (!audienceName) return null;

  return (
    <>
      <MyOrder showCheckoutBtn={false} />
    </>
  );
}

function BackButton() {
  const navigate = useNavigate();
  const { state } = useOrder();
  const { loading, orderDetails } = state;

  const urlParams = new URLSearchParams({
    ...orderDetails,
  });

  return (
    <Button
      type="button"
      secondary
      small
      onClick={() => navigate(`/dashboard/mywork/audiences`)}
      disabled={loading}
    >
      Back to Saved Audiences
    </Button>
  );
}